import { Topic } from 'lib/models/diagnoses/choices/topic';
import { Epidemiology } from 'lib/models/diagnoses/epidemiologies';
import { User } from 'lib/models/users/users';
import { stripHTML } from 'lib/utils/helpers/strip-html';
import { DateTime } from 'luxon';
import { Cause } from './cause';
import { Diagnosis } from './diagnosis';
import { LocalizedFinding, Pain, Rash, Swelling } from './localized-finding';
import { Reference } from './reference';
import { Anamnesis, Finding, Symptom } from './symptom';

export interface DiagnosisVersionConstructorOpts {
  id?: number;
  version?: number;
  diagnosisId?: number;

  isActive?: boolean;
  isHidden?: boolean;

  author?: User;
  comment?: string;
  created?: DateTime;

  name: string;
  synonyms: string[];
  topic?: Topic;

  description?: string;
  etiology?: string;
  approach?: string;
  treatment?: string;

  epidemiology?: string;
  epidemiologies?: Epidemiology[];

  causes?: Cause[];
  localizedCauses?: LocalizedFinding[];

  differentialDiagnoses?: Diagnosis[];
  references?: Reference[];
}

/**
 * A revision of a diagnosis.
 */
export class DiagnosisVersion {
  createEmpty(diagnosis?: Diagnosis) {
    return new DiagnosisVersion({
      name: diagnosis ? diagnosis.name : '',
      synonyms: diagnosis ? diagnosis.synonyms : []
    });
  }

  // --

  public readonly id: number | undefined;
  public readonly version: number | undefined;
  public readonly diagnosisId: number | undefined;

  public readonly isActive: boolean | undefined;
  public readonly isHidden: boolean | undefined;

  public readonly author: User | undefined;
  public readonly created: DateTime | undefined;
  public comment: string | undefined;

  public name: string;
  public synonyms: string[];
  public topic: Topic | undefined;
  public description: string;
  public etiology: string;
  public approach: string;
  public treatment: string;

  public epidemiology: string;
  public epidemiologies: Epidemiology[];

  public causes: Cause[];
  public localizedCauses: LocalizedFinding[];

  public differentialDiagnoses: Diagnosis[];
  public references: Reference[];

  constructor(opts: DiagnosisVersionConstructorOpts) {
    this.id = opts.id;
    this.version = opts.version;
    this.diagnosisId = opts.diagnosisId;

    this.isActive = opts.isActive;
    this.isHidden = opts.isHidden;

    this.author = opts.author;
    this.comment = opts.comment;
    this.created = opts.created;

    this.name = opts.name;
    this.synonyms = opts.synonyms;
    this.topic = opts.topic;
    this.description = opts.description || '';
    this.etiology = opts.etiology || '';
    this.approach = opts.approach || '';
    this.treatment = opts.treatment || '';

    this.epidemiology = opts.epidemiology || '';
    this.epidemiologies = opts.epidemiologies || [];

    this.causes = opts.causes || [];
    this.localizedCauses = opts.localizedCauses || [];

    this.differentialDiagnoses = opts.differentialDiagnoses || [];
    this.references = opts.references || [];
  }

  toString() {
    return (
      '<DiagnosisVersion: ' +
      this.id +
      ', version=' +
      this.version +
      ', diagnosisId=' +
      this.diagnosisId +
      '>'
    );
  }

  descriptionText(): string {
    return stripHTML(this.description);
  }

  etiologyText(): string {
    return stripHTML(this.etiology);
  }

  approachText(): string {
    return stripHTML(this.approach);
  }

  treatmentText(): string {
    return stripHTML(this.treatment);
  }

  epidemiologyText(): string {
    return stripHTML(this.epidemiology);
  }

  symptomList(): Symptom[] {
    return this.causes.filter((s) => s.isSymptom()).map((s) => s.asSymptom());
  }

  findingList(): Finding[] {
    return this.causes.filter((s) => s.isFinding()).map((s) => s.asFinding());
  }

  anamnesisList(): Anamnesis[] {
    return this.causes.filter((s) => s.isAnamnesis()).map((s) => s.asAnamnesis());
  }

  painList(): Pain[] {
    return this.localizedCauses.filter((l) => l.isPain()) as Pain[];
  }

  rashList(): Rash[] {
    return this.localizedCauses.filter((l) => l.isRash()) as Rash[];
  }

  swellingList(): Swelling[] {
    return this.localizedCauses.filter((l) => l.isSwelling()) as Swelling[];
  }

  toJson(): any {
    return {
      id: this.id,
      version: this.version,
      diagnosis: this.diagnosisId,

      is_active: this.isActive,
      is_hidden: this.isHidden,

      author: this.author ? this.author.toJson() : undefined,
      comment: this.comment || '',
      created: this.created ? this.created.toISO() : undefined,

      name: this.name,
      synonyms: this.synonyms,
      topic: this.topic?.id,
      description: this.description,
      etiology: this.etiology,
      approach: this.approach,
      treatment: this.treatment,

      epidemiology: this.epidemiology,
      epidemiologies: this.epidemiologies.map((e) => e.toJson()),

      causes: this.causes.map((c) => c.toJson()),
      localized_causes: this.localizedCauses.map((c) => c.toJson()),

      diff_diagnoses: this.differentialDiagnoses.map((d) => d.toJson()),

      references: this.references.map((r) => r.toJson())
    };
  }

  static fromJson(json: any): DiagnosisVersion {
    const opts: DiagnosisVersionConstructorOpts = {
      id: json.id || undefined,
      version: json.version || undefined,
      diagnosisId: json.diagnosis || undefined,

      isActive: json.is_active || undefined,
      isHidden: json.is_hidden || undefined,

      author: json.author ? User.fromJson(json.author) : undefined,
      comment: json.comment || '',
      created: json.date_created ? DateTime.fromISO(json.date_created) : undefined,

      name: json.name || '',
      synonyms: json.synonyms || [],
      topic: Topic.fromId(json.topic),
      description: json.description || undefined,
      etiology: json.etiology || undefined,
      approach: json.approach || undefined,
      treatment: json.treatment || undefined,

      epidemiology: json.epidemiology || undefined,
      epidemiologies: Array.isArray(json.epidemiologies)
        ? json.epidemiologies.map((e: any) => Epidemiology.fromJson(e))
        : [],

      causes: Array.isArray(json.causes)
        ? json.causes.map((e: any) => Cause.fromJson(e))
        : [],

      localizedCauses: Array.isArray(json.localized_causes)
        ? json.localized_causes.map((e: any) => LocalizedFinding.fromJson(e))
        : [],

      differentialDiagnoses: Array.isArray(json.diff_diagnoses)
        ? json.diff_diagnoses.map((d: any) => Diagnosis.fromJson(d))
        : [],

      references: Array.isArray(json.references)
        ? json.references.map((d: any, order: number) => Reference.fromJson(d, order))
        : []
    };

    return new DiagnosisVersion(opts);
  }
}
